import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./Auth-Context/AuthContext";
import { CompanyProvider } from "./Auth-Context/CompanyContext";
import { ToastProvider } from "./Auth-Context/ToastContext";
import ProtectedRoute from "./components/ProtectedRoute";
import LandingPage  from "./pages/LandingPage";
import LoginPage    from "./pages/LoginPage";
import Dashboard    from "./pages/Dashboard";
import Contacts     from "./pages/Contacts";
import Pipeline     from "./pages/Pipeline";
import Tasks        from "./pages/Tasks";
import Interactions from "./pages/Interactions";
import ProfilePage  from "./pages/ProfilePage";
import PricingPage  from "./pages/PricingPage";

import AdminLayout   from "./pages/admin/AdminLayout";
import AdminOverview from "./pages/admin/AdminOverview";
import AdminUsers    from "./pages/admin/AdminUsers";
import AdminLogs     from "./pages/admin/AdminLogs";

/* ── Admin-only guard ── */
function AdminGuard({ children }) {
  const { user, loading, isSessionValid } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-8 h-8 border-b-2 rounded-full animate-spin border-[#009DB9]"></div>
      </div>
    );
  }

  const isSuperAdmin = user?.role === "SUPER_ADMIN";

  if (!user || !isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  // Same 3h absolute-session check ProtectedRoute enforces for sales routes.
  // Without this, an expired SUPER_ADMIN session would still pass the
  // user/role checks above and reach admin pages.
  if (!isSessionValid()) {
    return <Navigate to="/login?reason=expired" replace />;
  }

  return children;
}

/* ── Guest-only guard (landing page and login page) ──
   A visitor who already has a valid session is sent straight to their home
   page instead of seeing the landing page or the login form again:
   SUPER_ADMIN -> /admin, everyone else -> /dashboard (the same rule
   LoginPage uses after a successful login).
   Both `user` and `isSessionValid()` must be checked. ProtectedRoute sends an
   expired session to /login?reason=expired while `user` is still set in
   state, so checking `user` alone would bounce between /login and /dashboard
   forever instead of showing the "session expired" banner. */
function GuestOnly({ children }) {
  const { user, loading, isSessionValid } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-8 h-8 border-b-2 rounded-full animate-spin border-[#009DB9]"></div>
      </div>
    );
  }

  if (user && isSessionValid()) {
    return <Navigate to={user.role === "SUPER_ADMIN" ? "/admin" : "/dashboard"} replace />;
  }

  return children;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <CompanyProvider>
            <Routes>
              {/* ── Public landing page ── */}
              <Route path="/" element={<GuestOnly><LandingPage /></GuestOnly>} />

              {/* ── Public pricing page ── */}
              <Route path="/pricing" element={<PricingPage />} />

              {/* ── Auth ── */}
              <Route path="/login" element={<GuestOnly><LoginPage /></GuestOnly>} />

              {/* ── Sales Agent Routes ── */}
              <Route path="/dashboard"    element={<ProtectedRoute><Dashboard    /></ProtectedRoute>} />
              <Route path="/contacts"     element={<ProtectedRoute><Contacts     /></ProtectedRoute>} />
              <Route path="/pipeline"     element={<ProtectedRoute><Pipeline     /></ProtectedRoute>} />
              <Route path="/tasks"        element={<ProtectedRoute><Tasks        /></ProtectedRoute>} />
              <Route path="/interactions" element={<ProtectedRoute><Interactions /></ProtectedRoute>} />

              {/* ── Profile Route for Sales Rep ── */}
              <Route path="/profile"      element={<ProtectedRoute><ProfilePage  /></ProtectedRoute>} />

              {/* ── Super Admin Routes ── */}
              <Route path="/admin"          element={<AdminGuard><AdminOverview /></AdminGuard>} />
              <Route path="/admin/users"    element={<AdminGuard><AdminUsers    /></AdminGuard>} />
              <Route path="/admin/logs"     element={<AdminGuard><AdminLogs     /></AdminGuard>} />

              {/* ── Profile Route for Super Admin — wrapped in AdminLayout so
                     the admin sidebar/topbar is used instead of the sales one. ── */}
              <Route
                path="/admin/profile"
                element={
                  <AdminGuard>
                    <AdminLayout title="My Profile" subtitle="Manage your account information and password">
                      <ProfilePage />
                    </AdminLayout>
                  </AdminGuard>
                }
              />

              {/* ── Fallback ── */}
              <Route path="*"  element={<Navigate to="/login" replace />} />
            </Routes>
          </CompanyProvider>
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;